/**
 * Revenu Québec fields for online orders.
 * A sale paid on the customer's device is a closing receipt (typTrans RFER, commerElectr O).
 * Pay at Store, e-Transfer, and Payment at Door stay unpaid until staff record the POS method.
 * That save sends the closing receipt with the recorded method. The order does not also need to be complete.
 */

export const PLATFORM_LEGEND = 'COMMANDE PAR UNE PLATEFORME NUMÉRIQUE';

/** Filter the browser console with this prefix. */
export const ONLINE_ORDER_MEV_TEST = 'Online order MEV test';

export function logOnlineOrderMev(step: string, detail?: Record<string, unknown>): void {
  if (detail) console.log(ONLINE_ORDER_MEV_TEST, step, detail);
  else console.log(ONLINE_ORDER_MEV_TEST, step);
}

const PLATFORM_TOKENS = ['UBER', 'DOORDASH', 'DOOR DASH', 'SKIP', 'FANTUAN', 'LVT'];
const ONLINE_PROCESSORS = ['stripe', 'paypal', 'checkout', 'apple', 'google', 'authorize', 'fiserv', 'sgpay'];

export type OnlineFiscalAction =
  | { kind: 'none' }
  | { kind: 'closing' }
  | { kind: 'reproduction' }
  | { kind: 'cancel_before_print' }
  | { kind: 'cancel_after_print' };

export interface OnlineFiscalSource {
  orderChannel: string;
  channel: string;
  serviceType: string;
  onlineOrderChannel: string;
  platform: string;
  paymentStatus: string;
  paymentMethod: unknown;
  paymentMethodCode: string;
  isPaid: unknown;
  customerEmail: string;
  onlineStatus: string;
  status: unknown;
  psiNoTrans: string;
  rqOriginalSent: unknown;
  rqSendClaimedAt: unknown;
  rqCancelSent: unknown;
}

function asText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return [record.provider, record.code, record.name, record.id, record.label, record.method]
      .map(asText)
      .filter(Boolean)
      .join(' ');
  }
  return '';
}

export function fiscalSource(order: any): OnlineFiscalSource {
  const nested = order?.order;
  const payment = order?.financials?.payment;
  return {
    orderChannel: asText(order?.orderChannel || nested?.order_channel),
    channel: asText(order?.channel || order?.sourceChannel),
    serviceType: asText(order?.serviceType || order?.fulfillment?.serviceType),
    onlineOrderChannel: asText(order?.onlineOrderChannel),
    platform: asText(order?.platform),
    paymentStatus: asText(order?.paymentStatus || payment?.status).toLowerCase(),
    paymentMethod: order?.paymentMethodRaw ?? order?.paymentMethod ?? payment?.method ?? nested?.payment_method,
    paymentMethodCode: asText(order?.paymentMethodCode),
    isPaid: order?.isPaid,
    customerEmail: asText(order?.receiptEmail || order?.customerEmail || order?.customer?.email),
    onlineStatus: asText(order?.onlineStatus || order?.storeProcessStatus).toUpperCase(),
    status: order?.status,
    psiNoTrans: asText(order?.psiNoTrans),
    rqOriginalSent: order?.rqOriginalSent,
    rqSendClaimedAt: order?.rqSendClaimedAt,
    rqCancelSent: order?.rqCancelSent,
  };
}

function channelBlob(source: OnlineFiscalSource): string {
  return [source.orderChannel, source.channel, source.platform, source.onlineOrderChannel]
    .join(' ')
    .toUpperCase();
}

function methodBlob(source: OnlineFiscalSource): string {
  return [asText(source.paymentMethod), source.paymentMethodCode].join(' ').toLowerCase();
}

export function isPlatformChannel(order: any): boolean {
  const blob = channelBlob(fiscalSource(order));
  return PLATFORM_TOKENS.some((token) => blob.includes(token));
}

export function isMerchantOnlineOrder(order: any): boolean {
  const source = fiscalSource(order);
  return source.channel.toUpperCase() === 'ONLINE' || source.orderChannel.toUpperCase() === 'ONLINE';
}

export function isOnlineFiscalOrder(order: any): boolean {
  return isMerchantOnlineOrder(order) || isPlatformChannel(order);
}

export type StaffSettlement = 'store' | 'etransfer' | 'door';

/** Payment the store confirms. These sales are not paid on the customer's device. */
export function staffSettlementOf(order: any): StaffSettlement | null {
  if (!isMerchantOnlineOrder(order) || isPlatformChannel(order)) return null;
  const blob = settlementBlob(order);
  if (!blob.trim()) return null;
  if (ONLINE_PROCESSORS.some((token) => blob.includes(token))) return null;
  if (
    blob.includes('payment_at_door')
    || blob.includes('payment at door')
    || blob.includes('pay_at_door')
    || blob.includes('pay at door')
  ) return 'door';
  if (blob.includes('etransfer') || blob.includes('e-transfer') || blob.includes('interac')) return 'etransfer';
  if (
    blob.includes('pay_at_store')
    || blob.includes('pay at store')
    || blob.includes('pay_at_counter')
    || blob.includes('counter')
    || blob.includes('custom')
    || blob.includes('cod')
    || /(^|[^a-z])(cash|card)([^a-z]|$)/.test(blob)
    || blob.includes('cash-')
    || blob.includes('card-')
  ) return 'store';
  return null;
}

export function isPayOnFulfillment(order: any): boolean {
  if (staffSettlementOf(order)) return true;
  const blob = methodBlob(fiscalSource(order));
  if (ONLINE_PROCESSORS.some((token) => blob.includes(token))) return false;
  return /(^|[^a-z])(cash|card|cod)([^a-z]|$)/.test(blob) || blob.includes('cash-') || blob.includes('card-');
}

export function isOrderPaid(order: any): boolean {
  const source = fiscalSource(order);
  if (source.isPaid === false) return false;
  if (source.isPaid === true) return true;
  return source.paymentStatus === 'succeeded' || source.paymentStatus === 'paid';
}

/** Order and payment both finished on the customer's device, on the merchant site or app. */
export function paidOnCustomerDevice(order: any): boolean {
  if (!isMerchantOnlineOrder(order) || isPlatformChannel(order)) return false;
  if (isPayOnFulfillment(order)) return false;
  return isOrderPaid(order);
}

export function onlineStatusOf(order: any): string {
  const source = fiscalSource(order);
  if (source.onlineStatus === 'COMPLETED') return 'COMPLETE';
  if (source.onlineStatus === 'CANCELLED') return 'CANCELED';
  if (source.onlineStatus === 'ENROUTE') return 'EN_ROUTE';
  return source.onlineStatus;
}

export function isOrderComplete(order: any): boolean {
  return onlineStatusOf(order) === 'COMPLETE' || fiscalSource(order).status === 2;
}

export function isOrderCanceled(order: any): boolean {
  return onlineStatusOf(order) === 'CANCELED';
}

export function hasAcceptedBill(order: any): boolean {
  const source = fiscalSource(order);
  return source.rqOriginalSent === true || Boolean(source.psiNoTrans);
}

function claimIsFresh(order: any): boolean {
  const at = Number(fiscalSource(order).rqSendClaimedAt || 0);
  return at > 0 && Date.now() - at < 120000;
}

export function commerElectrFor(order: any): 'O' | 'N' {
  return paidOnCustomerDevice(order) ? 'O' : 'N';
}

/** LVT platform, LVR merchant delivery, CMP merchant pickup. Null for in-store bills. */
export function onlineTypServ(order: any): 'LVT' | 'LVR' | 'CMP' | null {
  if (isPlatformChannel(order)) return 'LVT';
  if (!isMerchantOnlineOrder(order)) return null;
  const source = fiscalSource(order);
  const service = `${source.serviceType} ${source.onlineOrderChannel}`.toUpperCase();
  if (service.includes('DELIV')) return 'LVR';
  return 'CMP';
}

const RECORDED_PAYMENT_CODES = ['ARG', 'CRE', 'DEB', 'MIX', 'TFD', 'MVO', 'CRY', 'CPR', 'FID', 'PAC', 'CHQ', 'AUT', 'COU'];

function recordedPaymentCode(order: any): string {
  const code = asText(order?.paymentMethodCode || order?.financials?.payment?.methodCode).toUpperCase();
  return RECORDED_PAYMENT_CODES.includes(code) ? code : '';
}

/** Checkout choice, before staff replace it with the method the customer used. */
function settlementBlob(order: any): string {
  const checkout = [asText(order?.onlineCheckoutMethod), asText(order?.onlineCheckoutLabel)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (checkout.trim()) return checkout;
  return methodBlob(fiscalSource(order));
}

/**
 * A recorded POS method is sent as its own code.
 * Until staff record one, a paid Pay at Store, e-Transfer, or Payment at Door sale uses AUT, TFD, or DEB.
 * Paid-on-device card payments use CRE.
 */
export function onlineModPai(order: any, current: string): string | null {
  if (!isOnlineFiscalOrder(order)) return null;
  const recorded = recordedPaymentCode(order);
  if (isOrderPaid(order) && recorded) return recorded;
  const settlement = staffSettlementOf(order);
  if (settlement) {
    if (!isOrderPaid(order)) return null;
    if (settlement === 'etransfer') return 'TFD';
    if (settlement === 'door') return 'DEB';
    if (settlement === 'store') return 'AUT';
  }
  const code = String(current || '').toUpperCase();
  if (code === 'ARG' || code === 'CRE' || code === 'DEB' || code === 'AUC') return null;
  if (isPayOnFulfillment(order)) {
    const blob = methodBlob(fiscalSource(order));
    if (blob.includes('debit')) return 'DEB';
    if (blob.includes('cash') || blob.includes('cod')) return 'ARG';
    if (blob.includes('card')) return 'CRE';
    return null;
  }
  if (paidOnCustomerDevice(order)) return 'CRE';
  return null;
}

export function customerEmailOf(order: any): string {
  return fiscalSource(order).customerEmail.trim();
}

export type MevUnconfirmedRule = 'manual' | 'complete' | 'time';

export interface MevUnconfirmedPaymentSetting {
  rule?: MevUnconfirmedRule | string;
  delayMinutes?: number;
  defaultPaymentMethodId?: string;
  defaultPaymentMethodLabel?: string;
  defaultPaymentMethodCode?: string;
}

function orderCreatedMs(order: any): number {
  const value = order?.created;
  if (value?.toDate) {
    const date = value.toDate();
    return date instanceof Date ? date.getTime() : 0;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  return 0;
}

/** Staff never updated the payment, and the owner's rule says to send now. */
export function unconfirmedFallbackDue(
  order: any,
  setting: MevUnconfirmedPaymentSetting | null | undefined,
  now = Date.now(),
): boolean {
  const rule = String(setting?.rule || 'manual');
  if (rule === 'manual') return false;
  if (!staffSettlementOf(order) || isOrderPaid(order) || hasAcceptedBill(order) || isOrderCanceled(order)) return false;
  const code = String(setting?.defaultPaymentMethodCode || '').trim();
  if (!code) return false;
  if (rule === 'complete') return isOrderComplete(order);
  if (rule === 'time') {
    const minutes = Number(setting?.delayMinutes);
    const created = orderCreatedMs(order);
    if (!Number.isFinite(minutes) || minutes <= 0 || !created) return false;
    return now - created >= minutes * 60000;
  }
  return false;
}

function isUberChannel(order: any): boolean {
  return channelBlob(fiscalSource(order)).includes('UBER');
}

/** Uber prints the kitchen ticket on accept. The closing receipt waits until the order is ready. */
function uberClosingDue(order: any): boolean {
  const progress = order?.orderProgress && typeof order.orderProgress === 'object' ? order.orderProgress : {};
  if (progress.ready === true || progress.onRoute === true || progress.arrived === true || progress.delivered === true) return true;
  const status = [order?.uberOrderStatus, progress.lastStatus, fiscalSource(order).onlineStatus].map(asText).join(' ').toUpperCase();
  if (status.includes('READY') || status.includes('ROUTE') || status.includes('DELIVER') || status.includes('COMPLETE')) return true;
  const online = onlineStatusOf(order);
  return online === 'READY' || online === 'EN_ROUTE' || online === 'ON_ROUTE' || online === 'COMPLETE';
}

function shouldSendClosingNow(order: any): boolean {
  if (paidOnCustomerDevice(order)) return true;
  if (isUberChannel(order)) return isOrderPaid(order) && uberClosingDue(order);
  if (isPlatformChannel(order) && isOrderPaid(order)) return true;
  if (staffSettlementOf(order)) return isOrderPaid(order);
  return isOrderComplete(order) && isOrderPaid(order);
}

export function nextOnlineFiscalAction(order: any, options: { paper?: boolean; emailCopy?: boolean } = {}): OnlineFiscalAction {
  if (!order || !isOnlineFiscalOrder(order)) return { kind: 'none' };
  if (isOrderCanceled(order)) {
    if (fiscalSource(order).rqCancelSent === true || isOrderPaid(order)) return { kind: 'none' };
    return { kind: 'cancel_after_print' };
  }
  if (options.paper || options.emailCopy) return hasAcceptedBill(order) ? { kind: 'reproduction' } : { kind: 'none' };
  if (hasAcceptedBill(order) || claimIsFresh(order)) return { kind: 'none' };
  if (shouldSendClosingNow(order)) return { kind: 'closing' };
  return { kind: 'none' };
}
